import { useEffect, useRef, useState } from "react";

type UseExitIntentOptions = {
  /** Storage key for the frequency cap. Vary per popup if you mount more than one. */
  storageKey?: string;
  /** How many days to suppress the popup after it last showed. */
  frequencyCapDays?: number;
  /** Minimum time on page before any trigger can fire. */
  minTimeMs?: number;
  /** Minimum time on page before the desktop exit-intent (cursor out the top)
   *  can fire. Deliberately NOT `minTimeMs`: that one is 20–25s at every call
   *  site and is itself trigger 1's timer, so gating exit-intent behind it
   *  would make exit-intent dead code. */
  exitIntentMinTimeMs?: number;
  /** Minimum scroll depth (0..1) before any trigger can fire. */
  minScrollFraction?: number;
  /** Disable the hook entirely (e.g. when used inside a demo with a manual open). */
  disabled?: boolean;
  /** QA/dev override: open immediately and skip the frequency cap. Wire to a
   *  `?popup=force` URL param so the popup can be retested on any machine. */
  force?: boolean;
};

const DEFAULTS = {
  storageKey: "reminderPopup:lastShownAt",
  frequencyCapDays: 1,
  minTimeMs: 30_000,
  exitIntentMinTimeMs: 4_000,
  minScrollFraction: 0.4,
};

/**
 * Independent OR-triggered popup launcher.
 *
 * Fires the FIRST time any of these conditions is met (frequency cap permitting):
 *   1. Visitor has been on the page ≥ minTimeMs.
 *   2. Visitor has scrolled past minScrollFraction of the page (mobile only).
 *   3. Cursor leaves through the top edge of the viewport (desktop only),
 *      after exitIntentMinTimeMs on the page.
 *
 * Frequency cap (last-shown timestamp in localStorage) suppresses repeat
 * showings within frequencyCapDays. The timestamp is written automatically
 * the moment a trigger fires (markShown used to be the consumer's job, but
 * no consumer ever called it — leaving the cap permanently dead code and
 * re-modaling returning visitors on every pageview).
 *
 * Returns { shouldShow, markShown, dismiss, reset }.
 *   - markShown(): manually re-persist the "shown" timestamp (rarely needed —
 *                  fire() already writes it; kept for edge cases).
 *   - dismiss():   close it without resetting the cap (used by close button / submit).
 *   - reset():     clear the localStorage cap (useful for the demo page).
 */
export function useExitIntent(options: UseExitIntentOptions = {}) {
  const {
    storageKey,
    frequencyCapDays,
    minTimeMs,
    exitIntentMinTimeMs,
    minScrollFraction,
    disabled,
    force,
  } = { ...DEFAULTS, ...options };

  const [shouldShow, setShouldShow] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (disabled || typeof window === "undefined") return;

    // Force override (?popup=force): open immediately, skip the frequency cap.
    if (force) {
      firedRef.current = true;
      setShouldShow(true);
      return;
    }

    // Skip the frequency cap on localhost so the popup can be re-tested
    // repeatedly without clearing browser storage. No effect in production.
    const isLocalhost =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    // ── Frequency cap check ────────────────────────────────────────────────
    try {
      const last = window.localStorage.getItem(storageKey);
      if (!isLocalhost && last) {
        const lastMs = Number(last);
        const elapsedDays = (Date.now() - lastMs) / (1000 * 60 * 60 * 24);
        if (Number.isFinite(elapsedDays) && elapsedDays < frequencyCapDays) {
          return; // suppressed
        }
      }
    } catch {
      // localStorage may be unavailable (privacy mode); fall through and allow firing.
    }

    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      setShouldShow(true);
      // Persist the frequency-cap timestamp at fire time so the popup
      // respects its own cap on the next visit. (Deliberately not written
      // on the ?popup=force QA path, which returns before fire() exists.)
      try {
        window.localStorage.setItem(storageKey, String(Date.now()));
      } catch {
        // localStorage unavailable (privacy mode) — popup shows uncapped.
      }
    };

    const isMobile = window.innerWidth < 768;
    const mountedAt = Date.now();

    // Trigger 1: time on page (any device).
    const timer = setTimeout(fire, minTimeMs);

    // Trigger 2: scroll depth ≥ minScrollFraction (mobile only).
    let onScroll: (() => void) | null = null;
    if (isMobile) {
      onScroll = () => {
        const docHeight = Math.max(
          document.documentElement.scrollHeight - window.innerHeight,
          1,
        );
        if (window.scrollY / docHeight >= minScrollFraction) {
          fire();
          if (onScroll) window.removeEventListener("scroll", onScroll);
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    // Trigger 3: cursor leaves through the top edge (desktop only).
    //
    // 2026-09-09: this had NO time gate at all, so reaching for the address
    // bar, a bookmark or the tab strip in the first half-second of a pageview
    // opened the popup on top of a page the visitor had not seen yet — the
    // single loudest "this site feels buggy" moment. exitIntentMinTimeMs
    // (4s) is the dwell it now needs; it is still far below minTimeMs, so a
    // visitor who lands and bolts still gets the popup, just not instantly.
    let onMouseOut: ((e: MouseEvent) => void) | null = null;
    if (!isMobile) {
      onMouseOut = (e: MouseEvent) => {
        if (e.relatedTarget !== null) return;
        if (e.clientY > 0) return;
        if (Date.now() - mountedAt < exitIntentMinTimeMs) return;
        fire();
      };
      document.addEventListener("mouseout", onMouseOut);
    }

    return () => {
      clearTimeout(timer);
      if (onScroll) window.removeEventListener("scroll", onScroll);
      if (onMouseOut) document.removeEventListener("mouseout", onMouseOut);
    };
  }, [disabled, storageKey, frequencyCapDays, minTimeMs, exitIntentMinTimeMs, minScrollFraction, force]);

  const markShown = () => {
    try {
      window.localStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // ignore
    }
  };

  const dismiss = () => setShouldShow(false);

  const reset = () => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    firedRef.current = false;
    setShouldShow(false);
  };

  return { shouldShow, setShouldShow, markShown, dismiss, reset };
}
